import { createContext, useContext, useState, useEffect, useId, useMemo } from 'react';
import { useVersionedApi } from '../../utils/versionedApi';
import { getAuthToken } from '../../utils/localStorage';
import SearchableSelect from '../SearchableSelect';
import { EVENTS, captureEvent } from '../../config/analyticsEvents';

// A program form may host its scoped selector in the window header. Avoid
// displaying/fetching a second, broader selector inside that inactive editor.
export const FormClientHeaderContext = createContext(false);

/**
 * ClinicalPatientSearch - Reusable patient selection component for clinical documents
 * 
 * MANDATORY FEATURES (as of Nov 23, 2025):
 * - Filters for Behavioral Health program patients only
 * - Searchable dropdown with live filtering
 * - 10px font size for all text (clinical document standard)
 * - Compact design for modal/window forms
 * 
 * @param {Object} props
 * @param {Function} props.onPatientSelect - Callback when patient is selected: (patient) => void
 * @param {string} props.selectedPatientId - Currently selected patient ID (optional)
 * @param {boolean} props.disabled - Disable the search (optional)
 * @param {boolean} props.readOnly - Show selected patient but don't allow changes (optional)
 * @param {Object} props.selectedPatient - Pre-selected patient object (optional)
 * 
 * @example
 * <ClinicalPatientSearch
 *   onPatientSelect={(patient) => {
 *     setFormData(prev => ({
 *       ...prev,
 *       clientNumber: patient.patientNumber,
 *       clientName: patient.name
 *     }));
 *   }}
 *   selectedPatientId={formData.patientId}
 * />
 */
export default function ClinicalPatientSearch({
  onPatientSelect,
  selectedPatientId,
  disabled = false,
  readOnly = false,
  selectedPatient: initialSelectedPatient = null,
  programFilter = 'Behavioral Health',
  programId = null,
  includeInactive = false,
  patients: scopedPatients = null,
  label = 'Search Client',
  inverse = false,
  // Clients chosen elsewhere on the same form (a group note's other
  // participants). They are not offered; the current choice always is.
  excludePatientIds = null,
}) {
  const hasClientHeader = useContext(FormClientHeaderContext);
  const clientLabelId = useId();
  const apiClient = useVersionedApi('v1');
  const hasAuthToken = Boolean(getAuthToken());
  
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(initialSelectedPatient);
  const [loadingClients, setLoadingClients] = useState(true);

  // Fetch available clients (only Behavioral Health program patients)
  useEffect(() => {
    const fetchClients = async () => {
      if (hasClientHeader) { setLoadingClients(false); return; }
      setLoadingClients(true);
      
      // Skip API call if no token available (e.g., in React Native context)
      if (!hasAuthToken && !Array.isArray(scopedPatients)) {
        console.warn('ClinicalPatientSearch: No auth token available, skipping patient fetch');
        setLoadingClients(false);
        return;
      }
      
      try {
        const response = Array.isArray(scopedPatients) ? { data: scopedPatients } : await apiClient.get('/patients', {
          // The versioned client attaches the current token. Keep credentials
          // out of hook dependencies (React development warnings print them).
          params: { ...(includeInactive ? {} : { status: 'active' }), ...(programFilter ? { programType: programFilter } : {}), ...(programId ? { programId } : {}) }
        });

        // Handle both paginated { data: [...] } and flat array responses
        const allPatients = response.data?.data || (Array.isArray(response.data) ? response.data : []);

        // Format for SearchableSelect component
        const clients = allPatients.map((patient) => {
          // Format DOB properly for HTML date inputs (YYYY-MM-DD format)
          let formattedDOB = '';
          if (patient.dob) {
            try {
              const dobDate = new Date(patient.dob);
              // Use UTC methods to avoid timezone offset issues
              const year = dobDate.getUTCFullYear();
              const month = String(dobDate.getUTCMonth() + 1).padStart(2, '0');
              const day = String(dobDate.getUTCDate()).padStart(2, '0');
              formattedDOB = `${year}-${month}-${day}`;
            } catch (e) {
              console.error('Client date could not be formatted');
            }
          }
          
          return {
            value: patient.id,
            label: `${patient.firstName} ${patient.lastName}`,
            name: `${patient.firstName} ${patient.lastName}`,
            dob: formattedDOB, // YYYY-MM-DD format for date inputs
            patientNumber: patient.patientNumber || patient.patientId || '',
            // Include full patient object for callback
            patientData: patient
          };
        });
        
        setAvailableClients(clients);
        
        // Auto-select if selectedPatientId is provided
        if (selectedPatientId && !selectedClient) {
          const client = clients.find(c => c.value === selectedPatientId);
          if (client) {
            setSelectedClient(client);
          }
        }
      } catch (error) {
        console.error('Client list could not be loaded');
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [hasAuthToken, apiClient, selectedPatientId, includeInactive, programFilter, programId, scopedPatients, hasClientHeader]);

  // Update selected client when selectedPatientId changes externally
  useEffect(() => {
    if (selectedPatientId && availableClients.length > 0) {
      const client = availableClients.find(c => c.value === selectedPatientId);
      if (client && (!selectedClient || selectedClient.value !== selectedPatientId)) {
        setSelectedClient(client);
      }
    }
  }, [selectedPatientId, availableClients]);

  // ✅ FIX (Nov 24, 2025): Update selected client when parent passes selectedPatient prop (for draft loading)
  useEffect(() => {
    if (initialSelectedPatient && initialSelectedPatient.id) {
      // First try to find in availableClients
      if (availableClients.length > 0) {
        const client = availableClients.find(c => c.value === initialSelectedPatient.id);
        if (client && (!selectedClient || selectedClient.value !== initialSelectedPatient.id)) {
          setSelectedClient(client);
          return;
        }
      }

      // If not found in list but we have the data from draft, create a temporary entry
      // This handles cases where patient might not be in Behavioral Health program anymore
      if (!selectedClient || selectedClient.value !== initialSelectedPatient.id) {
        setSelectedClient({
          value: initialSelectedPatient.id,
          label: initialSelectedPatient.name || 'Unknown Client',
          name: initialSelectedPatient.name || 'Unknown Client',
          dob: initialSelectedPatient.dob || '',
          patientNumber: initialSelectedPatient.patientNumber || initialSelectedPatient.patientId || '',
          patientData: initialSelectedPatient
        });
      }
    }
  }, [initialSelectedPatient, availableClients]);

  const excludedKey = Array.isArray(excludePatientIds) ? excludePatientIds.filter(Boolean).join('|') : '';
  const selectableClients = useMemo(() => {
    if (!excludedKey) return availableClients;
    const excluded = new Set(excludedKey.split('|'));
    return availableClients.filter((client) => !excluded.has(client.value) || client.value === selectedClient?.value);
  }, [availableClients, excludedKey, selectedClient?.value]);

  const handleClientSelect = (clientValue) => {
    // SearchableSelect passes the value directly
    const client = availableClients.find((c) => c.value === clientValue);
    if (client) {
      setSelectedClient(client);

      // Product usage analytics: this is the genuinely patient-specific
      // selection component (SearchableSelect itself is also used for payer
      // pickers, insurance type pickers, etc., so the event does not belong
      // there). ClinicalPatientSearch does not have the raw search term at
      // this point (SearchableSelect owns and clears it internally), so 0 is
      // passed rather than inventing a value.
      captureEvent(EVENTS.PATIENT_SELECTED, { searchTermLength: 0 });

      // Call parent callback with patient data
      if (onPatientSelect) {
        onPatientSelect({
          id: client.value,
          name: client.name,
          patientNumber: client.patientNumber,
          dob: client.dob,
          // Include full patient data for advanced use cases
          fullData: client.patientData
        });
      }
    }
  };

  // Read-only display
  if (hasClientHeader) return null;
  if (readOnly && selectedClient) {
    return (
      <div className="mb-2">
        <label className="font-medium mb-0.5 text-gray-700 dark:text-gray-300 block" style={{fontSize: '10px'}}>
          Client
        </label>
        <div 
          className="w-full max-w-full box-border border border-gray-300 rounded-lg px-2 py-1 bg-gray-50 dark:bg-gray-900"
          style={{fontSize: '10px'}}
        >
          <span className="text-gray-900 dark:text-white">{selectedClient.label}</span>
        </div>
        <p className="text-gray-500 mt-0.5" style={{fontSize: '10px'}}>
          Client is locked for this document
        </p>
      </div>
    );
  }

  return (
    <div className="mb-2">
      <span id={clientLabelId} className={`font-medium mb-0.5 block ${inverse ? 'text-slate-100' : 'text-gray-700 dark:text-gray-300'}`} style={{fontSize: '10px'}}>
        {label} <span className="text-red-500">*</span>
      </span>
      <SearchableSelect
        ariaLabelledBy={clientLabelId}
        value={selectedClient?.value || ''}
        onChange={handleClientSelect}
        options={selectableClients}
        placeholder="Select a client..."
        className="text-xs"
        required
        disabled={disabled || loadingClients || availableClients.length === 0}
      />
      <p className={`${inverse ? 'text-slate-300' : 'text-gray-500'} mt-0.5`} style={{fontSize: '10px'}}>
        {loadingClients
          ? `Loading ${programFilter || ''} clients...`
          : `${availableClients.length} ${programFilter || ''} client${availableClients.length !== 1 ? 's' : ''} available`}
      </p>
    </div>
  );
}
